import { useMemo, useState } from 'react';
import { simulationExportUrl } from '../../api/client';
import { projectUsesMassFlow } from '../../lib/projectNormalization';
import { persistDocumentAndRefreshValidation } from '../../lib/fixValidationIssues';
import {
  collectProductTypeLabels,
  humanizeValidationMessage,
} from '../../lib/validationMessages';
import { normalizeNodeGroups } from '../../lib/nodeGroups';
import { formatSimTimeSeconds } from '../../lib/simulationDuration';
import {
  collectStaleNodeGroupRefs,
  resolveIssueContext,
} from '../../lib/validationNavigation';
import { useEditorStore } from '../../store/editorStore';
import { useSimulationStore } from '../../store/simulationStore';
import type { SimulationSummary } from '../../types/scenario';
import type { ValidationIssue } from '../../types/validation';
import { issueFieldPath } from '../../types/validation';
import Spinner from '../ui/Spinner';

// ------------------------------------------------------------
// Simple SVG sparkline chart (no external dependency)
// ------------------------------------------------------------

function SparklineChart({
  title,
  series,
  color = '#60a5fa',
  height = 64,
}: {
  title: string;
  series: number[];
  color?: string;
  height?: number;
}) {
  if (series.length < 2) return null;

  const width = 280;
  const max = Math.max(...series, 1);
  const min = Math.min(...series, 0);
  const range = max - min || 1;

  const points = series
    .map((v, i) => {
      const x = (i / (series.length - 1)) * width;
      const y = height - ((v - min) / range) * (height - 8) - 4;
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <div className="rounded-lg border border-border bg-surface/30 p-3">
      <div className="mb-1 text-[10px] uppercase tracking-wide text-text-muted">
        {title}
      </div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full"
        style={{ height }}
        role="img"
        aria-label={title}
      >
        <polyline
          fill="none"
          stroke={color}
          strokeWidth="1.5"
          points={points}
        />
      </svg>
      <div className="mt-1 flex justify-between text-[10px] text-text-subtle">
        <span>min {min.toFixed(0)}</span>
        <span>max {max.toFixed(0)}</span>
      </div>
    </div>
  );
}

export default function SimulationResultsPanel() {
  const run = useSimulationStore((s) => s.run);
  const liveOverlay = useSimulationStore((s) => s.run.liveOverlay);
  const cancelSimulation = useSimulationStore((s) => s.cancelSimulation);
  const visualFps = useSimulationStore((s) => s.visualFps);
  const receivedVisualFps = useSimulationStore((s) => s.receivedVisualFps);
  const renderedVisualFps = useSimulationStore((s) => s.renderedVisualFps);
  const document = useEditorStore((s) => s.document);

  const summary = (run.summary ?? {}) as SimulationSummary;
  const usesMassFlow = projectUsesMassFlow(document);

  const routeName = (id: string) =>
    document?.routes.find((r) => r.id === id)?.name ?? id;

  const nodeName = (id: string) =>
    document?.nodes.find((n) => n.id === id)?.name ?? id;

  const latestMetrics = useMemo(() => {
    if (run.status === 'running' && liveOverlay) {
      return {
        in_system_count: liveOverlay.inSystemCount,
        total_output: liveOverlay.totalOutput,
        total_generated: liveOverlay.totalGenerated,
        output_totals: liveOverlay.outputTotals,
        output_arrival_log: liveOverlay.outputArrivalLog,
        sim_time: run.liveSimTime,
      };
    }
    if (run.timeseries.length === 0) return null;
    return run.timeseries[run.timeseries.length - 1].metrics;
  }, [run.status, run.timeseries, run.liveSimTime, liveOverlay]);

  const productLabels = useMemo(() => {
    if (!document) return new Map<string, string>();
    return collectProductTypeLabels(document);
  }, [document]);

  const chartData = useMemo(() => {
    const inSystem = run.timeseries.map(
      (p) => Number((p.metrics as Record<string, unknown>).in_system_count ?? 0),
    );

    const routeIds = document?.routes.map((r) => r.id) ?? [];
    const throughputByRoute: Record<string, number[]> = {};
    for (const routeId of routeIds) {
      throughputByRoute[routeId] = run.timeseries.map((p) => {
        const tp = (p.metrics as Record<string, unknown>).route_throughput as
          | Record<string, number>
          | undefined;
        return tp?.[routeId] ?? 0;
      });
    }

    const processorIds =
      document?.nodes.filter((n) => n.type === 'processor').map((n) => n.id) ?? [];
    const utilizationByProcessor: Record<string, number[]> = {};
    for (const procId of processorIds) {
      utilizationByProcessor[procId] = run.timeseries.map((p) => {
        const util = (p.metrics as Record<string, unknown>).processor_utilization as
          | Record<string, number>
          | undefined;
        return (util?.[procId] ?? 0) * 100;
      });
    }

    return { inSystem, throughputByRoute, utilizationByProcessor };
  }, [run.timeseries, document]);

  if (run.status === 'running') {
    const outputTotals = (latestMetrics?.output_totals ?? {}) as Record<
      string,
      number
    >;
    const outputArrivalLog = (latestMetrics?.output_arrival_log ??
      liveOverlay?.outputArrivalLog ??
      []) as Array<{
      sim_time: number;
      output_id: string;
      product_type_id: string;
    }>;

    const latestSimTime = run.liveSimTime > 0
      ? run.liveSimTime
      : run.timeseries.length > 0
        ? run.timeseries[run.timeseries.length - 1].sim_time
        : 0;
    const runningLabel = run.runUntilStopped
      ? `Running until stopped — sim time ${formatSimTimeSeconds(latestSimTime)}`
      : run.progressPct >= 100
        ? 'Finishing simulation…'
        : `Running simulation… ${Math.round(run.progressPct)}%`;

    return (
      <div className="space-y-4 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <Spinner label={runningLabel} />
          <button
            type="button"
            className="btn-danger-ghost px-3 py-1.5 text-xs transition hover:bg-danger/20"
            onClick={() => void cancelSimulation()}
          >
            Stop simulation
          </button>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-surface">
          {run.runUntilStopped ? (
            <div className="h-full w-full animate-pulse bg-accent/40" />
          ) : (
            <div
              className="h-full bg-accent transition-all duration-300"
              style={{ width: `${run.progressPct}%` }}
            />
          )}
        </div>

        {!liveOverlay && (
          <p className="text-sm text-text-muted">
            Connecting to live simulation stream…
          </p>
        )}

        {liveOverlay && (
          <p className="text-xs text-text-subtle">
            Visual target {visualFps} FPS
            {receivedVisualFps > 0
              ? ` · received ${Math.round(receivedVisualFps)} FPS · rendered ${Math.round(renderedVisualFps)} FPS`
              : ''}
            . Simulation speed is independent of visual frame rate.
          </p>
        )}

        {latestMetrics && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="In system"
              value={String(latestMetrics.in_system_count ?? 0)}
            />
            <StatCard
              label="Total output"
              value={String(
                Math.round(
                  Number(
                    latestMetrics.total_output ??
                      liveOverlay?.totalOutput ??
                      0,
                  ),
                ),
              )}
            />
            <StatCard
              label="Total generated"
              value={String(
                Math.round(
                  Number(
                    latestMetrics.total_generated ??
                      liveOverlay?.totalGenerated ??
                      0,
                  ),
                ),
              )}
            />
            <StatCard
              label="Sim time"
              value={`${Math.round(Number(latestMetrics.sim_time ?? 0))}s`}
            />
          </div>
        )}

        {Object.keys(outputTotals).length > 0 && (
          <section>
            <h3 className="panel-section-title mb-2">Live output totals</h3>
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-border text-text-muted">
                  <th className="py-1 pr-2">Output node</th>
                  <th className="py-1 text-right">Count</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(outputTotals).map(([nodeId, count]) => (
                  <tr key={nodeId} className="border-b border-border/50">
                    <td className="py-1.5 pr-2 text-text">{nodeName(nodeId)}</td>
                    <td className="py-1.5 text-right font-medium text-text">
                      {Math.round(count)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {outputArrivalLog.length > 0 && (
          <section>
            <h3 className="panel-section-title mb-2">Recent output arrivals</h3>
            <ul className="max-h-40 space-y-1 overflow-y-auto text-xs">
              {[...outputArrivalLog].reverse().slice(0, 12).map((entry, index) => (
                <li
                  key={`${entry.output_id}-${entry.sim_time}-${index}`}
                  className="rounded-md border border-border/60 bg-surface/40 px-2 py-1.5 text-text-muted"
                >
                  <span className="text-text">{nodeName(entry.output_id)}</span>
                  {' · '}
                  {productLabels.get(entry.product_type_id) ?? entry.product_type_id}
                  {' · '}
                  t={Math.round(entry.sim_time)}s
                </li>
              ))}
            </ul>
          </section>
        )}

        {chartData.inSystem.length >= 2 && (
          <SparklineChart
            title="In-system count (live)"
            series={chartData.inSystem}
            color="#34d399"
          />
        )}
      </div>
    );
  }

  if (run.status === 'error') {
    return (
      <SimulationErrorDisplay
        error={run.error}
        issues={run.issues}
        document={document}
      />
    );
  }

  if (run.status === 'cancelled' && run.timeseries.length === 0 && !run.summary) {
    return (
      <div className="space-y-4 p-4">
        <div className="alert-warning px-4 py-3 text-sm">
          Simulation cancelled at {Math.round(run.progressPct)}% progress.
        </div>
        {run.runId && <ExportButtons runId={run.runId} />}
      </div>
    );
  }

  if (run.status === 'idle' || (!run.summary && run.timeseries.length === 0)) {
    return (
      <p className="p-4 text-sm text-text-muted">
        Run a simulation from the Scenario tab to see results here.
      </p>
    );
  }

  const routeThroughput = (latestMetrics?.route_throughput ?? {}) as Record<
    string,
    number
  >;
  const routeOccupancy = (latestMetrics?.route_occupancy ?? {}) as Record<
    string,
    number
  >;
  const processorQueues = (latestMetrics?.processor_queue_lengths ?? {}) as Record<
    string,
    number
  >;
  const processorOutputLog = (latestMetrics?.processor_output_log ??
    []) as Array<Record<string, unknown>>;

  return (
    <div className="space-y-4 p-4">
      {run.status === 'cancelled' && (
        <div className="alert-warning px-4 py-3 text-sm">
          Simulation cancelled — showing partial results (
          {run.timeseries.length} steps).
        </div>
      )}
      {run.runId && (
        <div className="flex flex-wrap gap-2">
          <ExportButtons runId={run.runId} />
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Scenario" value={summary.scenario_name ?? 'Custom'} />
        <StatCard
          label="Items generated"
          value={String(Math.round(summary.total_generated ?? 0))}
        />
        {usesMassFlow && (summary.total_generated_mass_kg ?? 0) > 0 && (
          <StatCard
            label="Mass generated (kg)"
            value={String(Math.round(summary.total_generated_mass_kg ?? 0))}
          />
        )}
        <StatCard
          label="Total output"
          value={String(Math.round(summary.total_output ?? 0))}
        />
        <StatCard
          label="Peak in system"
          value={String(summary.peak_in_system ?? 0)}
        />
      </div>

      {chartData.inSystem.length >= 2 && (
        <section>
          <h3 className="panel-section-title mb-2">Charts</h3>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <SparklineChart
              title="In-system count"
              series={chartData.inSystem}
              color="#34d399"
            />
            {Object.entries(chartData.throughputByRoute)
              .slice(0, 3)
              .map(([routeId, series]) => (
                <SparklineChart
                  key={routeId}
                  title={`Throughput: ${routeName(routeId)}`}
                  series={series}
                  color="#60a5fa"
                />
              ))}
            {Object.entries(chartData.utilizationByProcessor)
              .slice(0, 3)
              .map(([procId, series]) => (
                <SparklineChart
                  key={procId}
                  title={`Utilization: ${nodeName(procId)}`}
                  series={series}
                  color="#fbbf24"
                />
              ))}
          </div>
        </section>
      )}

      {summary.output_totals && Object.keys(summary.output_totals).length > 0 && (
        <section>
          <h3 className="panel-section-title mb-2">Output totals by category</h3>
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-border text-text-muted">
                <th className="py-1 pr-2">Node</th>
                <th className="py-1 pr-2">Category</th>
                <th className="py-1 text-right">Count</th>
              </tr>
            </thead>
            <tbody>
              {Object.values(summary.output_totals).map((o) => (
                <tr key={o.node_id} className="border-b border-border/50">
                  <td className="py-1.5 pr-2 text-text">{o.node_name}</td>
                  <td className="py-1.5 pr-2 text-text-muted">{o.category}</td>
                  <td className="py-1.5 text-right font-medium text-text">
                    {Math.round(o.count)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {Object.keys(routeThroughput).length > 0 && (
        <section>
          <h3 className="panel-section-title mb-2">
            Route throughput & occupancy (latest step)
          </h3>
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-border text-text-muted">
                <th className="py-1 pr-2">Route</th>
                <th className="py-1 pr-2 text-right">Throughput/min</th>
                <th className="py-1 text-right">Occupancy</th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(routeThroughput).map((routeId) => (
                <tr key={routeId} className="border-b border-border/50">
                  <td className="py-1.5 pr-2 text-text">{routeName(routeId)}</td>
                  <td className="py-1.5 pr-2 text-right text-text-muted">
                    {(routeThroughput[routeId] ?? 0).toFixed(1)}
                  </td>
                  <td className="py-1.5 text-right text-text-muted">
                    {(routeOccupancy[routeId] ?? 0).toFixed(0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {Object.keys(processorQueues).length > 0 && (
        <section>
          <h3 className="panel-section-title mb-2">Processor queue lengths</h3>
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-border text-text-muted">
                <th className="py-1 pr-2">Processor</th>
                <th className="py-1 text-right">Queue</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(processorQueues).map(([id, len]) => (
                <tr key={id} className="border-b border-border/50">
                  <td className="py-1.5 pr-2 text-text">{nodeName(id)}</td>
                  <td className="py-1.5 text-right text-text-muted">{len}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {processorOutputLog.length > 0 && (
        <section>
          <h3 className="panel-section-title mb-2">Processor output log</h3>
          <ul className="max-h-40 space-y-1 overflow-y-auto text-xs">
            {[...processorOutputLog].reverse().slice(0, 15).map((entry, i) => (
              <li
                key={i}
                className="rounded border border-border/50 bg-surface/20 px-2 py-1.5 text-text-muted"
              >
                <span className="font-medium text-text">
                  {nodeName(String(entry.processor_id ?? ''))}
                </span>
                <span className="text-text-subtle">
                  {' '}
                  @ {Math.round(Number(entry.sim_time ?? 0))}s →{' '}
                  {routeName(String(entry.route_id ?? ''))}
                  {entry.visual &&
                  typeof entry.visual === 'object' &&
                  (entry.visual as Record<string, unknown>).color
                    ? ` (${String((entry.visual as Record<string, unknown>).color)})`
                    : ''}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {summary.bottleneck_warnings && summary.bottleneck_warnings.length > 0 && (
        <section>
          <h3 className="panel-section-title mb-2">Bottleneck warnings</h3>
          <ul className="space-y-1 text-xs">
            {summary.bottleneck_warnings.map((w, i) => (
              <li
                key={`${w.node_id}-${i}`}
                className="alert-warning px-2 py-1.5"
              >
                <span className="font-medium">{w.node_name}</span>
                <span className="text-warning-fg/90">
                  {' '}
                  @ {Math.round(w.sim_time)}s — {w.message}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {summary.gate_log && summary.gate_log.length > 0 && (
        <section>
          <h3 className="panel-section-title mb-2">Gate change log</h3>
          <ul className="space-y-1 text-xs">
            {summary.gate_log.map((entry, i) => (
              <li
                key={`${entry.gate_id}-${i}`}
                className="rounded border border-border/50 bg-surface/20 px-2 py-1.5 text-text-muted"
              >
                <span className="font-medium text-text">{entry.gate_name}</span>
                {' → '}
                <span
                  className={
                    entry.state === 'open' ? 'text-success-on-dark' : 'text-danger'
                  }
                >
                  {entry.state}
                </span>
                <span className="text-text-subtle">
                  {' '}
                  @ {Math.round(entry.sim_time)}s ({entry.source})
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function SimulationErrorDisplay({
  error,
  issues,
  document,
}: {
  error: string | null;
  issues: ValidationIssue[];
  document: ReturnType<typeof useEditorStore.getState>['document'];
}) {
  const readOnly = useEditorStore((s) => s.permission) !== 'edit';
  const setNodeSelection = useEditorStore((s) => s.setNodeSelection);
  const removeStaleNodeGroupReference = useEditorStore(
    (s) => s.removeStaleNodeGroupReference,
  );
  const pruneAllStaleNodeGroupReferences = useEditorStore(
    (s) => s.pruneAllStaleNodeGroupReferences,
  );
  const [fixState, setFixState] = useState<
    'idle' | 'working' | 'saved' | 'error'
  >('idle');
  const [fixMessage, setFixMessage] = useState<string | null>(null);

  const applyNodeGroupFix = async (
    applyFix: () => void,
    successMessage: string,
  ) => {
    setFixState('working');
    setFixMessage(null);
    applyFix();
    const result = await persistDocumentAndRefreshValidation();
    if (result.error) {
      setFixState('error');
      setFixMessage(result.error);
      return;
    }
    if (result.ok) {
      setFixState('saved');
      setFixMessage(successMessage);
      return;
    }
    setFixState('idle');
    setFixMessage(
      `${result.remainingIssues.length} validation error(s) still remain.`,
    );
  };

  const staleRefs = useMemo(
    () => collectStaleNodeGroupRefs(issues, document),
    [issues, document],
  );

  const productLabels = useMemo(
    () => collectProductTypeLabels(document),
    [document],
  );

  const nodeName = (id: string) =>
    document?.nodes.find((n) => n.id === id)?.name ?? id;

  const goToNode = (nodeId: string) => {
    setNodeSelection([nodeId], { focus: true });
  };

  const showGroupOnCanvas = (groupIndex: number) => {
    if (!document) return;
    const group = normalizeNodeGroups(document)[groupIndex];
    if (!group) return;
    const validIds = group.node_ids.filter((id) =>
      document.nodes.some((node) => node.id === id),
    );
    if (validIds.length === 0) return;
    setNodeSelection(validIds, { focus: true });
  };

  if (issues.length === 0) {
    return (
      <div className="p-4">
        <div className="alert-danger px-4 py-3 text-sm">
          {error ?? 'Simulation failed'}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 p-4">
      <div className="alert-danger px-4 py-3 text-sm">
        <div className="font-medium text-white">Simulation blocked by project validation errors</div>
        <div className="mt-1 text-text-muted">
          Fix the issues below, save the project, then run simulation again.
          Node group errors usually mean a deleted node is still listed in a group.
        </div>
      </div>

      {staleRefs.length > 0 && !readOnly && (
        <div className="rounded-lg border border-warning/35 bg-warning-muted/20 px-4 py-3 text-sm text-warning-fg">
          <div className="font-medium">
            {staleRefs.length} stale node group reference
            {staleRefs.length === 1 ? '' : 's'} found
          </div>
          <button
            type="button"
            className="btn-ghost mt-2 px-3 py-1.5 text-xs disabled:opacity-50"
            disabled={fixState === 'working'}
            onClick={() =>
              void applyNodeGroupFix(
                () => pruneAllStaleNodeGroupReferences(),
                'Stale references removed and project saved. You can run simulation again.',
              )
            }
          >
            {fixState === 'working'
              ? 'Saving…'
              : 'Remove all stale references'}
          </button>
        </div>
      )}

      {fixMessage && (
        <div
          className={`rounded-lg px-4 py-3 text-sm ${
            fixState === 'error'
              ? 'alert-danger'
              : 'border border-success/35 bg-success-muted/20 text-success-fg'
          }`}
        >
          {fixMessage}
        </div>
      )}

      {error && (
        <div className="alert-danger px-4 py-3 text-sm">
          {error}
        </div>
      )}
      <ul className="space-y-2">
        {issues.map((issue, index) => {
          const context = resolveIssueContext(issue, document);
          const displayName =
            issue.node_name ??
            (context.groupName ? `Group: ${context.groupName}` : undefined) ??
            (context.nodeId ? nodeName(context.nodeId) : undefined);
          const fieldPath = issueFieldPath(issue);
          return (
            <li
              key={`${issue.code}-${fieldPath ?? index}`}
              className="alert-danger px-4 py-3 text-sm"
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 flex-1 space-y-1">
                  {displayName && (
                    <div className="font-medium text-white">{displayName}</div>
                  )}
                  <div>{humanizeValidationMessage(issue.message, productLabels)}</div>
                  {context.summary && (
                    <div className="text-xs text-text-muted">{context.summary}</div>
                  )}
                  {fieldPath && (
                    <div className="text-xs text-text-muted">{fieldPath}</div>
                  )}
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  {context.action === 'go_to_node' && context.nodeId && (
                    <button
                      type="button"
                      className="btn-danger-ghost px-3 py-1.5 text-xs"
                      onClick={() => goToNode(context.nodeId!)}
                    >
                      Go to node
                    </button>
                  )}
                  {context.action === 'show_group' && context.groupIndex != null && (
                    <button
                      type="button"
                      className="btn-danger-ghost px-3 py-1.5 text-xs"
                      onClick={() => showGroupOnCanvas(context.groupIndex!)}
                    >
                      Show group
                    </button>
                  )}
                  {context.action === 'remove_stale_group_ref' &&
                    context.staleNodeId &&
                    !readOnly && (
                      <button
                        type="button"
                        className="btn-danger-ghost px-3 py-1.5 text-xs disabled:opacity-50"
                        disabled={fixState === 'working'}
                        onClick={() =>
                          void applyNodeGroupFix(
                            () =>
                              removeStaleNodeGroupReference(
                                context.staleNodeId!,
                                context.groupIndex,
                              ),
                            'Stale reference removed and project saved.',
                          )
                        }
                      >
                        {fixState === 'working' ? 'Saving…' : 'Remove reference'}
                      </button>
                    )}
                  {context.action === 'remove_stale_group_ref' &&
                    context.nodeId && (
                      <button
                        type="button"
                        className="btn-danger-ghost px-3 py-1.5 text-xs"
                        onClick={() => goToNode(context.nodeId!)}
                      >
                        Show related nodes
                      </button>
                    )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-surface/40 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-text-muted">
        {label}
      </div>
      <div className="text-lg font-semibold text-text">{value}</div>
    </div>
  );
}

function ExportButtons({ runId }: { runId: string }) {
  return (
    <>
      <a
        href={simulationExportUrl(runId, 'csv')}
        className="rounded-md border border-border bg-surface/50 px-3 py-1.5 text-xs text-text-muted transition hover:bg-surface-overlay hover:text-text"
        download
      >
        Export CSV
      </a>
      <a
        href={simulationExportUrl(runId, 'json')}
        className="rounded-md border border-border bg-surface/50 px-3 py-1.5 text-xs text-text-muted transition hover:bg-surface-overlay hover:text-text"
        download
      >
        Export JSON
      </a>
    </>
  );
}
